import { isNetworkCreateError } from '@/utils/http/networkCreateError';
import { logger } from '@/utils/logger/logger';
import {
  finishTracking,
  startTracking,
  type TrackingHandle,
  type TrackingKey,
} from '@heidi/runtime/performance';

// The shared shape of an autosave attempt (`document_update`, `note_update`):
// one instanced attempt per save request, autonomous so no intent. Each
// operation names its own failures, so the vocabulary stays operation-owned.

/** How a save request failed, before an operation gives it a name. */
export type SaveRequestFailure =
  | 'network_unavailable'
  | 'timeout'
  | 'server_error'
  | 'rejected'
  | 'forbidden'
  | 'interrupted'
  | 'unclassified';

/** Maps a save rejection to a bounded failure; the raw error is never forwarded. */
export const classifySaveRequestFailure = (error: unknown): SaveRequestFailure => {
  const { code, response } = (error ?? {}) as {
    code?: unknown;
    response?: { status?: unknown };
  };

  const status = response?.status;
  if (typeof status === 'number') {
    if (status === 401) return 'interrupted';
    if (status === 403) return 'forbidden';
    if (status === 408 || status === 504) return 'timeout';
    if (status >= 500) return 'server_error';
    if (status >= 400) return 'rejected';
  }

  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return 'timeout';
  // Desktop offline short-circuits the request as an axios `ERR_NETWORK`.
  if (code === 'ERR_NETWORK' || isNetworkCreateError(error)) {
    return 'network_unavailable';
  }
  return 'unclassified';
};

export const namedError = (name: string): Error => {
  const error = new Error(name);
  error.name = name;
  return error;
};

export type SaveRequestAttemptConfig<TEditor extends string> = {
  key: TrackingKey;
  /** Diagnostic only: RUM context. */
  editor: TEditor;
  /** The operation's own named error for a failure; never the raw value. */
  errorFor: (failure: SaveRequestFailure) => Error;
  /** Log prefix for a telemetry step that threw. */
  scope: string;
};

/**
 * Runs one save request as an attempt. The request's result or error passes
 * through unchanged, and telemetry never changes the save's outcome. Instanced,
 * so overlapping saves each report their own outcome.
 */
export const trackSaveRequestAttempt = async <TEditor extends string, T>(
  config: SaveRequestAttemptConfig<TEditor>,
  save: () => Promise<T>
): Promise<T> => {
  const safely = (step: string, run: () => void): void => {
    try {
      run();
    } catch (error) {
      logger.warn(`[${config.scope}] telemetry step failed`, {
        step,
        errorName: error instanceof Error ? error.name : typeof error,
      });
    }
  };

  let handle: TrackingHandle | undefined;
  safely('begin', () => {
    handle = startTracking({
      key: config.key,
      instanced: true,
      metadata: { editor: config.editor, trigger: 'autonomous' },
    });
  });

  let result: T;
  try {
    result = await save();
  } catch (error) {
    safely('fail', () => {
      if (!handle) return;
      const failure = classifySaveRequestFailure(error);
      finishTracking({
        handle,
        outcome: 'failed',
        error: config.errorFor(failure),
        metadata: {
          result: 'error',
          ...(failure === 'interrupted' ? { cancel_reason: 'unauthorized' } : {}),
        },
      });
    });
    throw error;
  }

  // The legacy HTTP handler resolves a dead-session 401 or a suspended-account
  // 403 with no response, after a logout or redirect. Nothing was saved; this
  // assumes a committed save always resolves its response, never an empty body.
  if (result == null) {
    safely('interrupted', () => {
      if (!handle) return;
      finishTracking({
        handle,
        outcome: 'failed',
        error: config.errorFor('interrupted'),
        metadata: { result: 'error', cancel_reason: 'interrupted' },
      });
    });
    return result;
  }

  safely('complete', () => {
    if (!handle) return;
    finishTracking({ handle, metadata: { result: 'success' } });
  });
  return result;
};
